import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Modal from '../components/ui/Modal';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StepperHeader from '../components/ui/StepperHeader';
import { retreatFlowSteps } from '../domain/retreatFlowSteps';
import { useRegistration } from '../store/RegistrationContext';
import retreatService from '../services/retreatService';
import api from '../services/api';

export default function ReviewPage() {
  const { state } = useRegistration();
  const navigate = useNavigate();
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!state.selectedRetreatId) {
    return <Navigate to="/retreat" replace />;
  }

  if (state.selectedStudentIds.length === 0) {
    return <Navigate to="/students" replace />;
  }

  const selectedStudents = state.students.filter(s => state.selectedStudentIds.includes(s.id));
  const selectedSessions = state.selectedSessions || [];

  const handleSubmit = async () => {
    if (!isAgreementChecked) {
      alert('Please accept the terms and conditions to proceed');
      return;
    }

    try {
      setIsSubmitting(true);

      // Get user data
      const userData = state.auth.user?.userData || state.auth.user;
      const userId = userData?.user_Id || userData?.User_Id;
      const userEmail = userData?.user_Email || userData?.User_Email || '';
      const userFirstName = userData?.user_First_Name || userData?.User_First_Name || '';
      const userLastName = userData?.user_Last_Name || userData?.User_Last_Name || '';
      const userPhone = userData?.user_Cell_Phone_No || userData?.User_Cell_Phone_No || '';

      // Create retreat registration records via API
      console.log('Creating retreat registration...');
      
      const campId = state.selectedRetreatId;
      
      // For each selected student, create registration using AddRetreatCampStudents
      // EXACT payload matching Swagger schema from StudentDTO.cs
      const userName = `${userFirstName} ${userLastName}`.trim() || userEmail;
      
      const registrationPromises = selectedStudents.map(async (student) => {
        const parsedStudentId = Number.parseInt(student?.id, 10);
        const studentIdForApi = Number.isFinite(parsedStudentId) ? parsedStudentId : 0;

        // EXACT field names from Swagger schema (YKBaseAPI.json lines 17008-17170)
        const studentData = {
          // camelCase fields (from JsonPropertyName)
          studentId: studentIdForApi,
          classId: 0,
          campId: campId,
          enrollmentId: 0,
          userId: userId,
          lastName: student.lastName || '',
          firstName: student.firstName || '',
          dateOfBirth: student.dateOfBirth || new Date().toISOString(),
          gender: student.gender || 'M',
          relationship: student.relationship || 'Self',
          status: 'Active',
          createdBy: userName,
          created: new Date().toISOString(),
          modifiedBy: userName,
          modified: new Date().toISOString(),
          // snake_case fields (no JsonPropertyName - use exact Swagger names)
          student_Date_Of_Birth1: student.dateOfBirth || null,
          student_Age: 0,
          parent_Last_Name: userLastName || null,
          parent_First_Name: userFirstName || null,
          student_Email: student.email || userEmail || null,
          student_Phone: student.phone || userPhone || null,
          student_Address_Line1: student.addressLine1 || null,
          student_Address_Line2: student.addressLine2 || null,
          student_City: student.city || null,
          student_Country: student.country || 'USA',
          state_Id: student.stateId || 0,
          student_Zip_Code: student.zipCode || 0,
          student_Internal_Comments: null,
          student_Occupation: null,
          student_Age_Group_Id: 0,
          is_Accommodation_Needed: 'N',
          seva_Opportunity_Id: 0,
          registerSession_Count: selectedSessions.length || 1,
          selected_Session_Count: selectedSessions.length || 1,
          selected_To_Pay_Amt: 0,
          extended_Amt: 0,
          registered_Session_Count: selectedSessions.length || 1,
          registered_To_Pay_Amt: 0,
          user_Email: userEmail || null
        };

        console.log('Calling AddRetreatCampStudents with payload:', studentData);
        const registration = await retreatService.addStudent(studentData);
        return registration;
      });

      // STEP 1: AddRetreatCampStudents - Returns Student_Id for each student
      const studentResults = await Promise.all(registrationPromises);
      console.log('Step 1 - Students saved:', studentResults);

      // STEP 2: AddRetreatRegistration - Create registration record for each student
      // This is the CRITICAL step that .NET does - creates Retreat_Registration record
      console.log('Step 2 - Creating Retreat Registration records...');
      
      const registrationResults = [];
      for (const studentResult of studentResults) {
        const studentId = (
          studentResult?.Student_Id ??
          studentResult?.student_Id ??
          studentResult?.studentId ??
          0
        );

        if (!studentId) {
          console.error('No Student_Id returned from AddRetreatCampStudents:', studentResult);
          continue;
        }

        // Call AddRetreatRegistration - EXACT Swagger schema (snake_case)
        const registrationData = {
          retreat_Registration_Id: 0,
          camp_Id: campId,
          student_Id: studentId,
          start_Date: new Date().toISOString(),
          end_Date: new Date().toISOString(),
          createdBy: userName,
          modifiedBy: userName,
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          isactive: 'Y'
        };

        console.log('Calling AddRetreatRegistration for Student_Id:', studentId);
        const regResult = await retreatService.addRetreatRegistration(registrationData);
        console.log('AddRetreatRegistration result:', regResult);
        
        registrationResults.push({
          studentId: studentId,
          retreatRegistrationId: regResult?.Retreat_Registration_Id ?? regResult?.retreat_Registration_Id ?? 0,
          result: regResult
        });
      }

      // Get first registration ID for confirmation
      const firstRegistrationId = registrationResults[0]?.retreatRegistrationId || 'N/A';
      console.log('Step 2 - Registration records created. First ID:', firstRegistrationId);

      // STEP 3: AddRetreatRegistrationDetail - Add visit details for each registration
      // This is called for EACH visit/session - EXACT flow from .NET line 870-904
      console.log('Step 3 - Adding registration details (visits)...');
      const campStartDate = selectedSessions?.[0]?.date || new Date().toISOString();
      const campEndDate = selectedSessions?.[selectedSessions.length - 1]?.date || new Date().toISOString();
      
      for (const regRecord of registrationResults) {
        const retreatRegId = regRecord.retreatRegistrationId;

        if (!retreatRegId || retreatRegId === 0) {
          console.error('No Retreat_Registration_Id for student:', regRecord.studentId);
          continue;
        }

        // EXACT Swagger schema for RetreatRegistrationDetailDTO (snake_case)
        const detailData = {
          retreat_Registration_Details_Id: 0,
          retreat_Registration_Id: retreatRegId,
          camp_Id: campId,
          student_Id: regRecord.studentId,
          start_Date: campStartDate,
          end_Date: campEndDate,
          no_Of_Days: selectedSessions.length || 1,
          registration_Completed: 0,
          is_Accommodation_Needed: 'N',
          is_Transport: 'N',
          accommodation_Fees: 0,
          registration_Fee: 0,
          createdBy: userName,
          modifiedBy: userName,
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          visits: String(selectedSessions.length || 1),
          isactive: 'Y'
        };

        console.log('Calling AddRetreatRegistrationDetail for Retreat_Registration_Id:', retreatRegId);
        const detailResult = await retreatService.addRetreatRegistrationDetail(detailData);
        console.log('AddRetreatRegistrationDetail result:', detailResult);
      }

      console.log('Step 3 - Registration details added successfully');

      // Fetch complete enrollment data from database
      console.log('Fetching complete enrollment data from database...');
      const enrollmentData = await retreatService.getUnpaidAttendees(userId, campId);
      console.log('Enrollment data fetched:', enrollmentData);

      // Helper function to calculate age from date of birth
      const calculateAge = (dobString) => {
        if (!dobString) return 0;
        const dob = new Date(dobString);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        return age;
      };

      // Helper function to format date
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      };

      // Send confirmation email using Event project pattern
      let receiptUrl = '';
      let emailSent = false;
      let emailErrorMessage = '';
      try {
        console.log('Sending confirmation email...');

        const emailTimestamp = new Date();
        const transactionDate = emailTimestamp.toLocaleDateString();
        const transactionTime = emailTimestamp.toLocaleTimeString();

        // Use enrollment data for complete attendee information
        // API returns snake_case field names per Swagger schema
        const attendeeNames = enrollmentData.map(e => 
          `${e.student_First_Name || e.Student_First_Name || ''} ${e.student_Last_Name || e.Student_Last_Name || ''}`.trim()
        ).join(', ');
        
        const sessionSchedule = enrollmentData.length > 0 && (enrollmentData[0].start_Date || enrollmentData[0].Start_Date)
          ? `${formatDate(enrollmentData[0].start_Date || enrollmentData[0].Start_Date)} - ${formatDate(enrollmentData[0].end_Date || enrollmentData[0].End_Date)}`
          : selectedSessions.map(s => s.dateString).join(', ');
        const safePhone = userPhone || 'N/A';
        const pickUpDate = selectedSessions?.[0]?.dateString || 'February 14, 2026';
        const pickUpTime = transactionTime || 'All Day';

        const emailAppName = process.env.REACT_APP_EMAIL_APP_NAME || process.env.REACT_APP_APP_NAME || 'Retreat';
        const emailTemplateName = process.env.REACT_APP_EMAIL_TEMPLATE_NAME || 'RetreatRegistrationConfirmation';

        const campName = campId === 149 ? 'Spiritual Retreat' : 'Retreat Camp';
        const centerName = 'Yugal Kunj Retreat Center';
        
        const emailData = {
          emailFrom: "string",
          emailBody: "string",
          emailSubject: "string",
          attachment: "Y",
          userPhoneNumber: userPhone || "",
          emailTo: userEmail,
          location: "YKRetreat",
          input: "string",
          appContent: {
            userName: `${userFirstName} ${userLastName}`.trim(),
            programName: campName,
            appSchedule: sessionSchedule,
            attendee: attendeeNames,
            date: transactionDate,
            time: transactionTime,
            orderType: "Retreat Registration",
            orderNo: firstRegistrationId?.toString() || "0",
            paymentMode: "Free Registration",
            amount: "0.00",
            email: userEmail,
            phone: safePhone,
            phoneNumber: safePhone,
            contactNumber: safePhone,
            pickupPerson: `${userFirstName} ${userLastName}`.trim() || 'N/A',
            pickupPersonContact: safePhone,
            instructions: sessionSchedule || 'N/A',
            itemData: campName,
            pickUpDate: pickUpDate,
            pickUpTime: pickUpTime,
            paymentType: 'Free Registration',
            billingAddress: userData?.homeAddress || userData?.HomeAddress || 'Not provided',
            address: userData?.homeAddress || userData?.HomeAddress || 'Not provided',
            pickupOrReceiver: `${userFirstName} ${userLastName}`.trim() || 'N/A',
            orderReady: sessionSchedule || 'Retreat Registration Confirmed',
            orderReadyLastLine: `Thank you for registering for ${campName}`,
            headingDate: pickUpDate,
            headingTime: pickUpTime,
            transactionId: 'FREE-REGISTRATION',
            transactionDate: transactionDate,
            transactionTime: transactionTime,
            startDate: "February 14, 2026",
            endDate: "February 17, 2026",
            location: centerName,
            centerName: centerName,
            campName: campName,
            attendees: enrollmentData.map(e => ({
              name: `${e.student_First_Name || e.Student_First_Name || ''} ${e.student_Last_Name || e.Student_Last_Name || ''}`.trim(),
              age: calculateAge(e.student_Date_Of_Birth || e.Student_Date_Of_Birth),
              gender: e.student_Gender || e.Student_Gender || 'M',
              visitDates: `${formatDate(e.start_Date || e.Start_Date)} - ${formatDate(e.end_Date || e.End_Date)}`,
              accommodation: (e.is_Accommodation_Needed || e.Is_Accommodation_Needed) === 'Y' ? 'Yes' : 'No'
            })),
            numberOfAttendees: enrollmentData.length,
            numberOfSessions: enrollmentData.length > 0 ? (enrollmentData[0].visits || enrollmentData[0].Visits) : selectedSessions.length
          }
        };

        const emailUrl = `/messageChannel/SendEmailMessage?appName=${emailAppName}&templateName=${emailTemplateName}&orgId=3`;
        console.log('Email API URL:', emailUrl);
        console.log('Email data:', JSON.stringify(emailData, null, 2));
        
        const emailResponse = await api.post(emailUrl, emailData);
        
        console.log('Email response:', emailResponse);
        emailSent = true;
        
        // Extract receipt URL from response
        if (typeof emailResponse === 'string') {
          receiptUrl = emailResponse;
        } else if (typeof emailResponse?.data === 'string') {
          receiptUrl = emailResponse.data;
        } else {
          receiptUrl = (
            emailResponse?.receiptUrl || emailResponse?.data?.receiptUrl ||
            emailResponse?.attachmentUrl || emailResponse?.data?.attachmentUrl ||
            emailResponse?.pdfUrl || emailResponse?.data?.pdfUrl ||
            ''
          );
        }
        
        console.log('Email sent successfully. Receipt URL:', receiptUrl);
      } catch (err) {
        console.error('Email failed (non-blocking):', err);
        const errorDetails = err?.response?.data || err?.message || '';
        emailErrorMessage = typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails);
        console.error('Email error details:', errorDetails);
      }

      // Navigate to confirmation page with complete enrollment data
      navigate('/confirmation', {
        state: {
          registrationId: firstRegistrationId,
          receiptUrl: receiptUrl,
          email: userEmail,
          emailSent,
          emailError: emailErrorMessage,
          attendees: enrollmentData.map(e => ({
            firstName: e.student_First_Name || e.Student_First_Name,
            lastName: e.student_Last_Name || e.Student_Last_Name,
            age: calculateAge(e.student_Date_Of_Birth || e.Student_Date_Of_Birth),
            gender: e.student_Gender || e.Student_Gender,
            dateOfBirth: e.student_Date_Of_Birth || e.Student_Date_Of_Birth
          })),
          sessions: enrollmentData.map(e => ({
            dateString: `${formatDate(e.start_Date || e.Start_Date)} - ${formatDate(e.end_Date || e.End_Date)}`,
            visits: e.visits || e.Visits
          })),
          centerName: 'Yugal Kunj Retreat Center',
          campName: campId === 149 ? 'Spiritual Retreat' : 'Retreat Camp'
        }
      });

    } catch (error) {
      console.error('Registration error:', error);
      alert('Failed to complete registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-32 pt-4">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-[28px] bg-white/70 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <StepperHeader
              title="Review & Confirm"
              subtitle="Please review your registration details"
              steps={retreatFlowSteps}
              activeIndex={3}
              onBack={() => navigate('/sessions')}
            />

            <div className="p-6">
              {/* Agreement Section */}
              <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm mb-6">
                <div className="px-5 pt-4 pb-3 border-b border-gray-200/70">
                  <div className="text-sm font-semibold text-gray-900 mb-3">Review Summary</div>
                  <div className="rounded-xl bg-violet-50/50 border border-violet-200 p-4">
                    <label className="flex items-start gap-3 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAgreementChecked}
                        onChange={(e) => setIsAgreementChecked(e.target.checked)}
                        className="mt-0.5 h-5 w-5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                      />
                      <div className="flex-1">
                        <span className="leading-relaxed font-medium text-gray-800">
                          I have reviewed all details above and agree to the retreat terms & conditions
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsAgreementModalOpen(true);
                          }}
                          className="block mt-1.5 text-[11px] font-semibold text-violet-700 hover:text-violet-800 hover:underline"
                        >
                          📄 View Full Agreement
                        </button>
                        {!isAgreementChecked && (
                          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                            ⚠️ Please check this box to proceed with registration
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Registration Details */}
                <div className="p-5 space-y-4">
                  {/* Attendees */}
                  <div className="rounded-2xl bg-white border border-gray-200 p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-3">Registered Attendees ({selectedStudents.length})</div>
                    <div className="space-y-2">
                      {selectedStudents.map((student, index) => (
                        <div key={index} className="flex items-center gap-3 bg-purple-50 px-3 py-2 rounded-lg">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {student.firstName?.charAt(0) || 'A'}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900">{student.firstName} {student.lastName}</p>
                            <p className="text-xs text-gray-600">{student.gender === 'M' ? 'Male' : 'Female'} • {student.dateOfBirth}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sessions */}
                  <div className="rounded-2xl bg-white border border-gray-200 p-4">
                    <div className="text-xs font-semibold text-gray-600 mb-3">Selected Session Dates ({selectedSessions.length})</div>
                    {selectedSessions.length > 0 ? (
                      <div className="space-y-2">
                        {selectedSessions.map((session, index) => (
                          <div key={index} className="flex items-center gap-3 bg-blue-50 px-3 py-2 rounded-lg">
                            <i className="fas fa-calendar-day text-blue-600"></i>
                            <p className="text-sm font-semibold text-gray-900">{session.dateString}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Full retreat period</p>
                    )}
                  </div>

                  {/* Fee Summary */}
                  <div className="rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-semibold text-gray-600">Registration Fee</div>
                        <div className="text-sm text-gray-700 mt-1">This is a free retreat registration</div>
                      </div>
                      <div className="text-3xl font-bold text-green-600">FREE</div>
                    </div>
                  </div>
                </div>
              </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer with Back and Continue buttons */}
      <Footer
        onBack={() => navigate('/sessions')}
        onContinue={handleSubmit}
        continueText="Confirm Registration"
        continueDisabled={!isAgreementChecked}
        isLoading={isSubmitting}
      />

      {/* Agreement Modal */}
      {isAgreementModalOpen && (
        <Modal
          isOpen={isAgreementModalOpen}
          onClose={() => setIsAgreementModalOpen(false)}
          title="Retreat Terms & Conditions"
        >
          <div className="prose prose-sm max-w-none">
            <h3>Retreat Registration Agreement</h3>
            <p>By registering for this retreat, you agree to the following terms and conditions:</p>
            <ul>
              <li>All attendees must follow retreat guidelines and schedules</li>
              <li>Respectful behavior is expected at all times</li>
              <li>The retreat center reserves the right to modify schedules as needed</li>
              <li>Cancellation policy applies as per retreat center rules</li>
              <li>Attendees are responsible for their personal belongings</li>
            </ul>
            <p className="font-semibold mt-4">This is a free retreat registration. No payment is required.</p>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => setIsAgreementModalOpen(false)}
              className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                setIsAgreementChecked(true);
                setIsAgreementModalOpen(false);
              }}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all"
            >
              I Agree
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
